import React, { useEffect } from 'react';
import NavBarItem from './NavBarItem';
import ImageLink from './ImageLink';
import betaImage from '../resources/get-beta-100px.png';
import { createExportUserGdprReportAction, IExportUserGdprReportAction, IProfileType } from '../actions/ProfileActions';
import { connect } from 'react-redux';
import { IState } from '../state/State';
import { NumberHelper } from '../helpers/NumberHelper';
import ProfileTable from './ProfileTable';
import LineChart from './charts/LineChart';
import HeatMapChart, { IHeatMapChartDataItem } from './charts/HeatMapChart';
import WordCloudChart from './charts/WordCloudChart';
import PyramidChart from './charts/PyramidChart';
import AngularGaugeChart from './charts/AngularGaugeChart';
import SolidGaugeChart from './charts/SolidGaugeChart';
import MapWithFlightsChart from './charts/MapWithFlightsChart';
import { IMaxDifficultyPassesOverTimeState, IPackProgressState, IPlaysOverTimeState, IPlaysPerTimeOfDayState, IRecommendedRivalsState, ISeasonsProgressState, IUserNextScoresToTakeState, userSeasonsProgressReducer } from '../reducers/StatsReducers';
import StepLineChart, { IStepLineChartSeriesProps } from './charts/StepLineChart';
import { IGetUserSeasonsProgressSucceededAction, IUserRecommendedRivalType } from '../actions/StatsActions';
import UserSeasonsProgressCharts from './UserSeasonsProgressCharts';
import { VennDiagram } from '@amcharts/amcharts4/plugins/venn';
import VennDiagramChart, { IVennDiagramChartDataItem } from './charts/VennDiagramChart';
import HorizontalDumbellPlotChart, { IHorizontalDumbellPlotChartDataItem } from './charts/HorizontalDumbellPlotChart';
import { StringHelper } from '../helpers/StringHelper';
import UserPackProgressCharts from './UserPackProgressCharts';
import { ICanExportGdprReportState } from '../reducers/ProfileReducers';
import GdprExportBody from './GdprExportBody';
import YearInReviewLinkBody from './YearInReviewLinkBody'
import YearlyRadarChart from './charts/YearlyRadarChart';

export interface IProfileBodyProps {
  profile: IProfileType,
  playsOverTime: IPlaysOverTimeState,
  playsPerTimeOfDay: IPlaysPerTimeOfDayState,
  maxDifficultyPassesOverTime: IMaxDifficultyPassesOverTimeState,
  seasonsProgress: ISeasonsProgressState,
  recommendedRivals: IRecommendedRivalsState,
  nextScoresToTake: IUserNextScoresToTakeState,
  packProgress: IPackProgressState,
}

const getSubscriptionStatus = (subscriptionStatus: number) : string => {
  switch (subscriptionStatus)
  {
    case 0: return "NONE";
    case 1: return "ACTIVE";
    case 2: return "INACTIVE";
    default: return "ERROR";
  }
}

const getWeekdayName = (weekdayIndex: number) => {
  switch (weekdayIndex) {
    case 0: return "Sunday";
    case 1: return "Monday";
    case 2: return "Tuesday";
    case 3: return "Wednesday";
    case 4: return "Thursday";
    case 5: return "Friday";
    case 6: return "Saturday";
    default: return "N/A";
  }
}

const getHourName = (hour: number) => {
  if (hour == 0) {
    return "12am"
  }
  else if (hour >= 1 && hour <= 11) {
    return `${hour}am`
  }
  else if (hour == 12) {
    return "12pm"
  }
  else {
    return `${hour-12}pm`
  }
}

const ProfileBody: React.FC<IProfileBodyProps> = (props: IProfileBodyProps) => {
    let options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    }

    let expirationDate = new Date(props.profile.subscription.expirationDate);
    let registrationDate = new Date(props.profile.registrationDate);
    let subscriptionValueClassName = props.profile.subscription.status == 1 ? "ProfileTableSubscriptionValidValue" : "ProfileTableSubscriptionExpiredValue";

    // Registration data.
    let registrationData: Array<[string, string]> = [];
    registrationData.push(["Registered On", registrationDate.toLocaleString(undefined, options)]);

    // Subscription data.
    let subscriptionData: Array<[string, string]> = [];
    subscriptionData.push(["Status", getSubscriptionStatus(props.profile.subscription.status)]);
    subscriptionData.push(["Expiration", props.profile.subscription.expirationDate == null ? "No Subscription" : expirationDate.toLocaleString(undefined, options)]);

    // Statistics data.
    let statisticsData: Array<[string, string]> = [];
    statisticsData.push(["Time Played", NumberHelper.getTimeLengthFromTicks(props.profile.statistics.timePlayedInTicks, false)]);
    statisticsData.push(["Total Taps", props.profile.statistics.totalTaps.toString()]);
    statisticsData.push(["Unique Charts Played", props.profile.statistics.chartsPlayed.toString()]);
    statisticsData.push(["Charts Written", props.profile.statistics.chartsWritten.toString()]);
    statisticsData.push(["Charts Rated", props.profile.statistics.chartsRated.toString()]);

    // Build all the charts based on available data.
    let playsOverTimeChart;
    if (props.playsOverTime !== undefined && props.playsOverTime.results?.length > 0) {
      playsOverTimeChart = <YearlyRadarChart id="PlaysOverTimeYearlyRadarChart" xAxisTitle="Date" yAxisTitle="Number of Plays" data={props.playsOverTime.results} />
    }
    else {
      playsOverTimeChart = "(loading...)";
    }

    let playsPerTimeOfDayChart;
    if (props.playsPerTimeOfDay !== undefined) {
      // Create array of all possible time slots, starting with 12am at the top
      let fullTimeSlots: IHeatMapChartDataItem[] = [];

      // Helper function to create a time entry with proper sorting
      const createTimeEntry = (hour: number): string => {
        // Create value that sorts correctly but displays nicely
        const timeLabel = getHourName(hour);
        return `${hour.toString().padStart(2, '0')}_${timeLabel}`;
      };
      
      // Add all hours (12am first, then 1am through 11pm)
      const hours = [0, ...Array.from({length: 23}, (_, i) => i + 1)];
      for (const hour of hours) {
        for (let day = 0; day < 7; day++) {
          fullTimeSlots.push({
            xAxisValue: getWeekdayName(day),
            yAxisValue: createTimeEntry(hour),
            value: 0
          });
        }
      }

      // Fill in actual play counts where they exist
      if (props.playsPerTimeOfDay.results) {
        props.playsPerTimeOfDay.results.forEach(item => {
          const timeEntry = createTimeEntry(item.hour);
          
          // Find matching slot and update its value
          const index = fullTimeSlots.findIndex(slot => 
            slot.xAxisValue === getWeekdayName(item.dayOfWeek) && 
            slot.yAxisValue === timeEntry
          );
          
          if (index !== -1) {
            fullTimeSlots[index].value = item.value;
          }
        });
      }

      playsPerTimeOfDayChart = <HeatMapChart id="WhatTimesYouPlayHeatMapChart" xAxisName="weekday" yAxisName="hour" data={fullTimeSlots} />
    }

    let maxDifficultyPassesOverTimeChart;
    if (props.maxDifficultyPassesOverTime !== undefined) {
      let singlesLineChartSeriesProps: IStepLineChartSeriesProps = {
        name: "Singles",
        data: props.maxDifficultyPassesOverTime.singlesMaxDifficultyPasses,
      }

      let doublesLineChartSeriesProps: IStepLineChartSeriesProps = {
        name: "Doubles",
        data: props.maxDifficultyPassesOverTime.doublesMaxDifficultyPasses,
      }

      maxDifficultyPassesOverTimeChart = <StepLineChart id="MaxDifficultyPassesStepLineChart" xAxisTitle="Date" yAxisTitle="Max Difficulty" data={[singlesLineChartSeriesProps, doublesLineChartSeriesProps]} />
    }

    let rivalsData: IVennDiagramChartDataItem[] = [
      { name: "Timing", value: 10, tooltipText: props.recommendedRivals.timingRivalUserName, sets: null },
      { name: "Difficulty", value: 10, tooltipText: props.recommendedRivals.difficultyRivalUserName, sets: null },
      { name: "Scores", value: 10, tooltipText: props.recommendedRivals.scoresRivalUserName, sets: null },
      { name: "Timing and Difficulty", value: 2, tooltipText: props.recommendedRivals.difficultyTimingRivalUserName, sets: ["Timing", "Difficulty"] },
      { name: "Timing and Scores", value: 2, tooltipText: props.recommendedRivals.scoresTimingRivalUserName, sets: ["Timing", "Scores"] },
      { name: "Difficulty and Scores", value: 2, tooltipText: props.recommendedRivals.scoresDifficultyRivalUserName, sets: ["Difficulty", "Scores"] },
      { name: "Timing, Difficulty, and Scores", value: 1, tooltipText: props.recommendedRivals.scoresDifficultyTimingRivalUserName, sets: ["Timing", "Difficulty", "Scores"] },
    ]

    let nextScoresData: IHorizontalDumbellPlotChartDataItem[] = props.nextScoresToTake.results.map((d) : IHorizontalDumbellPlotChartDataItem => { return {
      category: StringHelper.escapeAmchartsString(d.notechartName),
      openValue: d.userScore * 100,
      closeValue: d.nextScore * 100,
      difference: (d.nextScore * 100) - (d.userScore * 100),
      extraData1: d.nextScoreUserName,
      extraData2: StringHelper.escapeAmchartsString(d.songName),
    }});    

    return (
      <div className="ProfileBody">
        <p className="ProfileTitle">{props.profile.userName}</p>
        <p className="ProfileSubtitle">Registration</p>
        <ProfileTable valueClassName="ProfileTableRegistrationValue" data={registrationData} />
        <p className="ProfileSubtitle">Subscription</p>
        <ProfileTable valueClassName={subscriptionValueClassName} data={subscriptionData} />
        <p className="ProfileSubtitle">Statistics</p>
        <ProfileTable valueClassName="ProfileTableStatisticsValue" data={statisticsData} />
        <p className="ProfileSubtitle">Charts</p>
        <br/>
        <table className="ProfileChartsTable">
          <tr>
            <td colSpan={2}><UserSeasonsProgressCharts data={props.seasonsProgress.results} /></td>
          </tr>
          <tr>
            <td colSpan={2}><UserPackProgressCharts data={props.packProgress.results} /></td>
          </tr>
          <tr>
            <td colSpan={2}>Plays Over Time</td>
          </tr>
          <tr>
            <td colSpan={2}>{playsOverTimeChart}</td>
          </tr>
          <tr>
            <td colSpan={2}>Max Passed Chart Difficulty</td>
          </tr>
          <tr>
          <td colSpan={2}><AngularGaugeChart id="MaxPassedChartDifficultyAngularGaugeChart" minValue={0} maxValue={120} value={props.profile.statistics.maxDifficultyPassed} /></td>
          </tr>
          <tr>
            <td colSpan={2}>What Times You Play</td>
          </tr>
          <tr>
            <td colSpan={2}>{playsPerTimeOfDayChart}</td>
          </tr>
          <tr>
            <td>Max Difficulties Achieved Over Time</td>
            <td>Recommended Rivals</td>
          </tr>
          <tr>
            <td>{maxDifficultyPassesOverTimeChart}</td>
            <td><VennDiagramChart id="RecommendedRivalsVennDiagram" data={rivalsData} /></td>
          </tr>
          <tr>
            <td colSpan={2}>Scores To Take Next</td>
          </tr>
          <tr>
            <td colSpan={2}><HorizontalDumbellPlotChart id="RecommendedNextScoreHorizontalDumbellPlotChart" data={nextScoresData} tooltipText={"Your Score: [bold]{openValueX.value}[/]\n[bold]{extraData1}'s[/] Score: [bold]{valueX.value} (+{difference})[/]\nSong: [bold]{extraData2}[/]\nNotechart: [bold]{category}[/]"} /></td>
          </tr>
        </table>
        <YearInReviewLinkBody />
        <p className="ProfileSubtitle">Export Your Data</p>
        <GdprExportBody />
        {/* Total Plays per Time of Day and Day of the Week
        <HeatMapChart id="PlaysPerHourAndDayHeatMap" />
        Plays per Song
        <WordCloudChart id="PlayedSongsWordCloud" />
        Scores Above a Target %
        <PyramidChart id="ScoresAboveTargetPercentagePyramidChart" /> */}
      </div>
    );
  }

  const mapStateToProps = (state: IState) => ({
    profile: state.profile.result,
    playsOverTime: state.userPlaysOverTime,
    playsPerTimeOfDay: state.userPlaysPerTimeOfDay,
    maxDifficultyPassesOverTime: state.userMaxDifficultyPassesOverTime,
    seasonsProgress: state.userSeasonsProgress,
    recommendedRivals: state.userRecommendedRivals,
    nextScoresToTake: state.userNextScoresToTake,
    packProgress: state.userPackProgress,
  });
  
  export default connect(mapStateToProps)(ProfileBody);
